import { useEffect, useState } from 'react';
import { formatDateOnly } from '../../utils/date';
import { requisitosDaSubsecao } from '../../utils/catalogo';
import * as catalogoApi from '../../api/catalogo';
import * as avaliacoesApi from '../../api/avaliacoes';

const STATUS_ABREV = {
  supera: 'S',
  conforme: 'C',
  parcialmente_conforme: 'PC',
  nao_conforme: 'NC',
};

// Paralelo ao RelatorioImpressao (Exportar PDF): fica escondido na tela e só aparece no
// @media print — mas só quando body tem a classe "imprimir-checklist" (setada pelo botão
// "Gerar Checklist" em AbaChecklist.jsx), senão apareceria junto com o Relatório completo,
// que também vive fora do .tela-interativa e imprimiria sempre.
export default function ChecklistImpressao({ idHospital, hospital, selecionados }) {
  const [secoes, setSecoes] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const [resCatalogo, resAvaliacoes] = await Promise.all([
          catalogoApi.listarHierarquia(),
          avaliacoesApi.listarAtuais(idHospital),
        ]);
        if (cancelado) return;
        setSecoes(resCatalogo.data);
        setAvaliacoes(resAvaliacoes.data);
      } catch {
        // checklist de impressão — se falhar, a exportação sai incompleta silenciosamente,
        // sem travar a aba interativa (que já tem seu próprio tratamento de erro)
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [idHospital]);

  if (!secoes || !avaliacoes || selecionados.size === 0) {
    return <div className="checklist-impressao" />;
  }

  const avaliacaoPorRequisito = new Map(avaliacoes.map((a) => [a.id_requisito, a]));

  const secoesComRequisitos = secoes
    .map((secao) => {
      const linhas = secao.subsecoes.flatMap((sub) =>
        requisitosDaSubsecao(sub)
          .filter((r) => selecionados.has(r.id_requisito))
          .map((r) => ({ requisito: r, subsecao: sub }))
      );
      return { secao, linhas };
    })
    .filter((s) => s.linhas.length > 0);

  return (
    <div className="checklist-impressao">
      <div className="checklist-impressao__cabecalho">
        <div>
          <h1>{hospital.nome}</h1>
          <p>{hospital.cnpj || 'CNPJ não informado'}</p>
        </div>
        <div className="checklist-impressao__resumo">
          <div style={{ fontWeight: 700 }}>Checklist de Acompanhamento</div>
          <div style={{ fontSize: 11 }}>Gerado em {new Date().toLocaleDateString('pt-BR')}</div>
          <div style={{ fontSize: 11 }}>{selecionados.size} requisito(s) selecionado(s)</div>
        </div>
      </div>

      <div className="checklist-impressao__legenda">
        <strong>Legenda:</strong> S = Supera &nbsp;·&nbsp; C = Conforme &nbsp;·&nbsp; PC = Parcialmente
        Conforme &nbsp;·&nbsp; NC = Não Conforme
      </div>

      {secoesComRequisitos.map(({ secao, linhas }) => (
        <div key={secao.id_secao} className="checklist-impressao__secao">
          <h2>
            Seção {secao.numero} — {secao.nome}
          </h2>
          {linhas.map(({ requisito: r, subsecao: sub }) => {
            const avaliacao = avaliacaoPorRequisito.get(r.id_requisito);
            return (
              <div key={r.id_requisito} className="checklist-impressao__item">
                <div className="checklist-impressao__item-topo">
                  <div className="checklist-impressao__item-codigo">
                    {sub.numero} · {r.codigo}
                    {r.core ? ' · CORE' : ''}
                  </div>
                  <div className="checklist-impressao__opcoes">
                    <span>☐ S</span>
                    <span>☐ C</span>
                    <span>☐ PC</span>
                    <span>☐ NC</span>
                  </div>
                </div>
                <div className="checklist-impressao__item-texto">{r.texto}</div>
                <div className="checklist-impressao__item-ref">
                  Última avaliação:{' '}
                  {avaliacao
                    ? `${STATUS_ABREV[avaliacao.status] || avaliacao.status} · ${formatDateOnly(avaliacao.data_avaliacao)}`
                    : '—'}
                </div>
                <div className="checklist-impressao__linha">
                  <span>Fragilidade:</span>
                  <span className="checklist-impressao__preencher" />
                </div>
                <div className="checklist-impressao__linha">
                  <span>Ação proposta:</span>
                  <span className="checklist-impressao__preencher" />
                </div>
                <div className="checklist-impressao__linha checklist-impressao__linha--dupla">
                  <span className="checklist-impressao__linha-metade">
                    <span>Prazo da ação:</span>
                    <span className="checklist-impressao__preencher checklist-impressao__preencher--curto" />
                  </span>
                  <span className="checklist-impressao__linha-metade">
                    <span>Vigência (meses):</span>
                    <span className="checklist-impressao__preencher checklist-impressao__preencher--curto" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
